import React, { useEffect, useRef } from 'react'
import { Box, SxProps, Theme, Tooltip, TooltipProps } from '@mui/material'
import { TOptions } from 'i18next'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'

interface Props {
  placement?: TooltipProps['placement']
  titleKey?: string
  titleValue?: string | number | TOptions
  fullWidth?: boolean
  hasTooltip?: boolean
  sx?: SxProps<Theme>
  children?: React.ReactNode
  hideOnPrintClass?: string
}

/**
 * Allows to wrap content with a tooltip based on certain conditions
 */
const OptionalTooltipWrapper: React.FC<Props> = ({
  titleKey = '',
  titleValue,
  placement = 'top-end',
  children,
  fullWidth = false,
  hasTooltip = true,
  sx,
  hideOnPrintClass,
}) => {
  const TOUCH_DELAY_FOR_MOBILE = 0 // must be 0 so that tooltip is working on mobile devices
  const { t } = useCustomTranslation()
  const refTooltipContainer = useRef<HTMLDivElement>(null)
  const translationOptions = typeof titleValue === 'object' ? titleValue : { value: titleValue }
  const translatedTitle = titleKey && hasTooltip ? t(titleKey, translationOptions) : ''

  useEffect(() => {
    if (refTooltipContainer.current) {
      refTooltipContainer.current.title = translatedTitle
    }
  }, [translatedTitle, hasTooltip])

  if (!titleKey || !hasTooltip) return <>{children}</>

  return (
    <Tooltip title={translatedTitle} placement={placement} enterTouchDelay={TOUCH_DELAY_FOR_MOBILE} className={hideOnPrintClass}>
      <Box ref={refTooltipContainer} style={{ width: fullWidth ? '100%' : 'auto' }} sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
        {children}
      </Box>
    </Tooltip>
  )
}

export default OptionalTooltipWrapper
